import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useActionDialog } from '../../../../components/ui';
import type { TreatmentStage, VisualizationMediaItem } from '../../../../types/admin';
import {
  useDeleteTreatmentStageVisualizationMedia,
  useReorderTreatmentStageVisualizationMedia,
  useUploadTreatmentStageVisualizationMedia,
} from '../../hooks/usePatientMutations';
import { hasVideoExtension } from '../../../../utils/fileValidation';
import styles from '../StepPanel.module.scss';
import { MediaDropZone } from './MediaDropZone';
import { MediaGrid } from './MediaGrid';

const MAX_PHOTOS = 10;
const MAX_VIDEOS = 1;

export type VisualizationMediaSectionProps = {
  readonly patientId: string;
  readonly treatmentId: string;
  readonly stage: TreatmentStage;
};

/** Photos and the single walkthrough video the patient sees under the plans. */
export function VisualizationMediaSection({
  patientId,
  treatmentId,
  stage,
}: VisualizationMediaSectionProps) {
  const { t } = useTranslation();
  const { confirmAction } = useActionDialog();
  const uploadMediaMutation = useUploadTreatmentStageVisualizationMedia(
    patientId,
    treatmentId,
    stage.id,
  );
  const deleteMediaMutation = useDeleteTreatmentStageVisualizationMedia(
    patientId,
    treatmentId,
    stage.id,
  );
  const reorderMediaMutation = useReorderTreatmentStageVisualizationMedia(
    patientId,
    treatmentId,
    stage.id,
  );
  const [uploadError, setUploadError] = useState<string | null>(null);

  const sortedMedia = useMemo(
    () => [...stage.visualization_media].sort((a, b) => a.display_order - b.display_order),
    [stage.visualization_media],
  );

  const videoCount = sortedMedia.filter((m) => m.media_type === 'video').length;
  const photoCount = sortedMedia.filter((m) => m.media_type === 'photo').length;
  const remainingPhotoSlots = Math.max(0, MAX_PHOTOS - photoCount);
  const remainingVideoSlots = Math.max(0, MAX_VIDEOS - videoCount);

  async function handleFilesSelected(files: FileList | null) {
    if (!files || files.length === 0) return;
    setUploadError(null);

    let remainingVideos = remainingVideoSlots;
    let remainingPhotos = remainingPhotoSlots;
    const errors: string[] = [];

    for (const file of Array.from(files)) {
      const isVideo =
        file.type.startsWith('video/') ||
        ((!file.type || file.type === 'application/octet-stream') && hasVideoExtension(file.name));
      const isPhoto = file.type.startsWith('image/');

      if (!isVideo && !isPhoto) {
        errors.push(`${file.name}: unsupported file type`);
        continue;
      }

      if (isVideo) {
        if (remainingVideos <= 0) {
          errors.push(`${file.name}: ${t('modeling.videoLimitReached')}`);
          continue;
        }
        remainingVideos -= 1;
      } else {
        if (remainingPhotos <= 0) {
          errors.push(`${file.name}: ${t('modeling.photoLimitReached')}`);
          continue;
        }
        remainingPhotos -= 1;
      }

      try {
        await uploadMediaMutation.mutateAsync(file);
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Upload failed';
        errors.push(`${file.name}: ${message}`);
      }
    }

    if (errors.length > 0) {
      setUploadError(errors.join('\n'));
    }
  }

  async function handleDeleteMedia(item: VisualizationMediaItem) {
    if (deleteMediaMutation.isPending) return;
    if (
      !(await confirmAction({
        message: t('modeling.confirmDelete'),
        confirmLabel: t('dialog.delete'),
        tone: 'danger',
      }))
    ) {
      return;
    }
    deleteMediaMutation.mutate(item.id);
  }

  function handleReorder(orderedIds: readonly string[]) {
    // Detect no-op vs current order to avoid unnecessary request.
    const currentIds = sortedMedia.map((m) => m.id);
    if (
      orderedIds.length === currentIds.length &&
      orderedIds.every((id, idx) => id === currentIds[idx])
    ) {
      return;
    }
    reorderMediaMutation.mutate(orderedIds);
  }

  const isBusy =
    uploadMediaMutation.isPending ||
    deleteMediaMutation.isPending ||
    reorderMediaMutation.isPending;

  return (
    <div className={styles.mediaSection}>
      <header className={styles.sectionHeader}>
        <div className={styles.sectionHeaderText}>
          <h4 className={styles.title}>{t('modeling.visualizationMedia')}</h4>
          <p className={styles.subtitle}>{t('modeling.dragToReorder')}</p>
        </div>
        <span className={styles.sectionCountChip}>
          {t('modeling.mediaCountSummary', { videos: videoCount, photos: photoCount })}
        </span>
      </header>

      {sortedMedia.length > 0 && (
        <MediaGrid
          items={sortedMedia}
          patientId={patientId}
          treatmentId={treatmentId}
          stageId={stage.id}
          onDelete={handleDeleteMedia}
          onReorder={handleReorder}
          isMutating={isBusy}
          deletingId={
            deleteMediaMutation.isPending ? (deleteMediaMutation.variables ?? null) : null
          }
        />
      )}

      <MediaDropZone
        isUploading={uploadMediaMutation.isPending}
        onFilesSelected={(files) => void handleFilesSelected(files)}
      />

      {uploadError && <p className={styles.inlineError}>{uploadError}</p>}
      {uploadMediaMutation.error instanceof Error && !uploadError && (
        <p className={styles.inlineError}>{uploadMediaMutation.error.message}</p>
      )}
      {deleteMediaMutation.error instanceof Error && (
        <p className={styles.inlineError}>{deleteMediaMutation.error.message}</p>
      )}
      {reorderMediaMutation.error instanceof Error && (
        <p className={styles.inlineError}>{reorderMediaMutation.error.message}</p>
      )}
    </div>
  );
}
